/**
 * Tenant logo loading shared across every vector PDF, so the technical report
 * and the prescription report embed the same tenant-branded logo (the canvas
 * exports already draw it directly).
 *
 * Returns a JPEG (flattened over `backgroundColor`, i.e. the header navy) that
 * can be embedded as a PDF image XObject via `getDataUrlImageHex`.
 */

const loadImage = (source) =>
  new Promise((resolve) => {
    if (!source) {
      resolve(null)
      return
    }

    const image = new Image()
    let settled = false
    const finish = (value) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      resolve(value)
    }
    const timeoutId = window.setTimeout(() => finish(null), 5000)
    image.onload = () => finish(image)
    image.onerror = () => finish(null)

    image.src = source
  })

export const getTenantLogoSources = () => {
  const buildVersion = process.env.REACT_APP_BUILD_VERSION
  const sources = []
  const variants = ['logo-white.png', 'logo-header.png', 'logo.png']
  const splashLogo = typeof document !== 'undefined'
    ? document.querySelector('#splash-screen img')
    : null
  // The server replaces the HTML build token at runtime. A prebuilt JS bundle
  // can have no build version (or an older one), so use the actual tenant URL.
  try {
    const page = new URL(document.baseURI)
    const source = new URL(splashLogo?.getAttribute?.('src') || splashLogo?.src, page)
    if (source.origin === page.origin && /\/tenant\/[^/]+\/media\/logos\/[^/]+$/.test(source.pathname)) {
      variants.forEach((name) => {
        const variant = new URL(source.href)
        variant.pathname = source.pathname.replace(/[^/]+$/, name)
        variant.hash = ''
        sources.push(variant.href)
      })
      source.hash = ''
      sources.push(source.href)
      return [...new Set(sources)]
    }
  } catch (error) {
    // Non-browser callers and missing/malformed runtime URLs use build config.
  }

  if (buildVersion) {
    const publicUrl = String(process.env.PUBLIC_URL || '').replace(/\/$/, '')
    variants.forEach((name) => sources.push(`${publicUrl}/tenant/${encodeURIComponent(buildVersion)}/media/logos/${name}`))
  }
  return sources
}

const channelLuminance = (value) => {
  const channel = value / 255
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
}

const rgbLuminance = (red, green, blue) =>
  0.2126 * channelLuminance(red) + 0.7152 * channelLuminance(green) + 0.0722 * channelLuminance(blue)

const getColorLuminance = (color) => {
  const value = String(color || '').trim()

  if (value.startsWith('rgb')) {
    const parts = value.match(/\d+(\.\d+)?/g)?.slice(0, 3).map(Number)

    if (parts?.length === 3) {
      return rgbLuminance(...parts)
    }
  }

  const normalized = value.replace('#', '')
  const hex = normalized.length === 3
    ? normalized.split('').map((part) => `${part}${part}`).join('')
    : normalized.padStart(6, '0').slice(0, 6)
  const red = parseInt(hex.slice(0, 2), 16)
  const green = parseInt(hex.slice(2, 4), 16)
  const blue = parseInt(hex.slice(4, 6), 16)

  if (![red, green, blue].every(Number.isFinite)) {
    return 1
  }

  return rgbLuminance(red, green, blue)
}

const getImageLuminance = (image) => {
  const width = image.naturalWidth || image.width
  const height = image.naturalHeight || image.height

  if (!width || !height) {
    return null
  }

  try {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')
    if (!context) return null
    context.clearRect(0, 0, width, height)
    context.drawImage(image, 0, 0, width, height)

    const data = context.getImageData(0, 0, width, height).data
    const histogram = new Map()
    let count = 0

    for (let index = 0; index < data.length; index += 4) {
      const alpha = data[index + 3] / 255

      if (alpha < 0.08) {
        continue
      }

      // Quantize the foreground colors to bound work for large tenant logos.
      // Transparent padding must not influence the choice of logo variant.
      const key = (data[index] >> 4) * 256 + (data[index + 1] >> 4) * 16 + (data[index + 2] >> 4)
      const color = histogram.get(key) || {weight: 0, total: 0}
      color.weight += alpha
      color.total += rgbLuminance(data[index], data[index + 1], data[index + 2]) * alpha
      histogram.set(key, color)
      count += alpha
    }

    return count > 0
      ? [...histogram.values()].map(({weight, total}) => ({weight: weight / count, luminance: total / weight}))
      : null
  } catch (error) {
    return null
  }
}

const getContrastScore = (logoLuminance, backgroundLuminance) => {
  const bright = Math.max(logoLuminance, backgroundLuminance)
  const dark = Math.min(logoLuminance, backgroundLuminance)

  return (bright + 0.05) / (dark + 0.05)
}

const getForegroundContrast = (colors, backgroundLuminance) => {
  // Ignore the small anti-aliased fringe, but do not let a bright emblem hide
  // an unreadable dark wordmark on a multicolor tenant logo.
  const scores = colors.map(({weight, luminance}) => ({weight, score: getContrastScore(luminance, backgroundLuminance)}))
    .sort((a, b) => a.score - b.score)
  let weight = 0
  for (const item of scores) {
    weight += item.weight
    if (weight >= 0.1) return item.score
  }
  return 0
}

const createLogoPlate = (image, color) => {
  try {
    const width = image.naturalWidth || image.width
    const height = image.naturalHeight || image.height
    const padding = Math.max(2, Math.round(height * 0.1))
    const canvas = document.createElement('canvas')
    canvas.width = width + padding * 2
    canvas.height = height + padding * 2
    const context = canvas.getContext('2d')
    if (!context) return null
    context.fillStyle = color
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image, padding, padding, width, height)
    return canvas
  } catch (error) {
    return null
  }
}

export const loadContrastingTenantLogoElement = async (backgroundColor = '#ffffff') => {
  const sources = getTenantLogoSources()
  const backgroundLuminance = getColorLuminance(backgroundColor)
  let best = null
  let bestColors = null
  let bestScore = -1
  // Parallel loads keep missing optional variants within the overall PDF
  // deadline, rather than consuming one full timeout per missing asset.
  const images = await Promise.all(sources.map(loadImage))
  for (const image of images) {

    if (!image) {
      continue
    }

    const colors = getImageLuminance(image)
    // A tainted/unmeasurable image must not taint the entire downloadable
    // canvas. Let the caller use its existing vector/text fallback instead.
    if (!colors) continue
    const score = getForegroundContrast(colors, backgroundLuminance)

    if (score > bestScore) {
      best = image
      bestColors = colors
      bestScore = score
    }
  }

  if (!best) return null
  if (bestScore >= 3) return best
  const dominant = bestColors.reduce((largest, color) => color.weight > largest.weight ? color : largest)
  const lightScore = getContrastScore(dominant.luminance, getColorLuminance('#ffffff'))
  const darkScore = getContrastScore(dominant.luminance, getColorLuminance('#101426'))
  // Keep brand colors intact when the tenant has only one unsuitable variant.
  return createLogoPlate(best, lightScore >= darkScore ? '#ffffff' : '#101426')
}

export const loadTenantLogoImage = async (backgroundColor = '#ffffff') => {
  const logo = await loadContrastingTenantLogoElement(backgroundColor)

  if (!logo) {
    return null
  }

  try {
    const canvas = document.createElement('canvas')
    const width = logo.naturalWidth || logo.width
    const height = logo.naturalHeight || logo.height

    if (!width || !height) return null

    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')
    if (!context) return null
    context.fillStyle = backgroundColor
    context.fillRect(0, 0, width, height)
    context.drawImage(logo, 0, 0, width, height)

    return {dataUrl: canvas.toDataURL('image/jpeg', 0.94), height, width}
  } catch (error) {
    return null
  }
}

export const getDataUrlImageHex = (dataUrl) => {
  if (!dataUrl || !String(dataUrl).startsWith('data:image/jpeg;base64,')) {
    return ''
  }

  const binary = atob(String(dataUrl).split(',')[1] || '')
  let hex = ''

  for (let index = 0; index < binary.length; index += 1) {
    hex += binary.charCodeAt(index).toString(16).padStart(2, '0').toUpperCase()
  }

  return hex
}
